import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  Workflow,
  Clock,
  FileText,
  AlertTriangle,
  MessageSquare,
  Lock,
  Database,
  Zap,
  TrendingDown,
  Terminal,
  Shield
} from 'lucide-react';
import Navbar from './Navbar';

const OpsTriageIntelligence = () => {
  const navigate = useNavigate();
  const [showStickyButton, setShowStickyButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const scrollPercentage = scrollPosition / (documentHeight - windowHeight);
      setShowStickyButton(scrollPercentage > 0.5);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    navigate('/?subject=Operational%20Intelligence%20Inquiry', { replace: true });
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const benefits = [
    {
      icon: Clock,
      title: '60-Second Fact Sheets',
      description: 'Generate comprehensive technical context in under a minute—no more digging through documentation or asking the client for details.'
    },
    {
      icon: TrendingDown,
      title: 'Automated Triage',
      description: 'Automatic fact-sheet generation, based on your docs, previous solutions, and the current system state, attached right to your ticket.'
    },
    {
      icon: MessageSquare,
      title: 'Natural Language Queries',
      description: 'Ask "What\'s the backup configuration for this client?" and get instant answers pulled from your KB, tickets, and current configuration.'
    },
    {
      icon: FileText,
      title: 'Contextual Documentation',
      description: 'AI synthesizes scattered documentation into actionable technical fact sheets—no more context switching across tools.'
    },
    {
      icon: Lock,
      title: 'Privacy-First Architecture',
      description: 'Your data stays under your control. Read-only access with zero data retention. Process with your chosen AI model (self-hosted or cloud-based under your agreement).'
    },
    {
      icon: Zap,
      title: 'Ready to Deploy',
      description: 'Pre-engineered workflow designed for operations teams—deployed in days with proven integration patterns.'
    }
  ];

  const integrations = [
    {
      name: 'Hudu',
      features: [
        'Asset database synchronization',
        'Network topology mapping',
        'Password vault integration (read-only)',
        'Configuration documentation'
      ]
    },
    {
      name: 'Confluence',
      features: [
        'Internal knowledge base articles',
        'Procedure documentation',
        'Change log history',
        'Project documentation'
      ]
    },
    {
      name: 'Ticketing Systems',
      features: [
        'Historical ticket patterns',
        'Resolution procedures',
        'Client communication logs',
        'Escalation paths'
      ]
    }
  ];

  const useCases = [
    {
      question: 'What\'s the backup configuration for ClientX?',
      answer: 'The system cross-references Hudu asset data, Confluence procedures, and recent backup-related tickets to provide complete backup configuration, schedule, retention policy, and last successful backup timestamp.'
    },
    {
      question: 'Has this issue happened before for this client?',
      answer: 'By analyzing ticket history and resolution notes, the system identifies similar past incidents and recommends proven resolution steps—saving hours of troubleshooting.'
    },
    {
      question: 'What VPN credentials does this user need?',
      answer: 'The system synthesizes access control lists from Hudu, VPN documentation from Confluence, and user-specific permissions to generate a complete access fact sheet.'
    },
    {
      question: 'Should this ticket be escalated to Level 2?',
      answer: 'Based on issue complexity, historical resolution patterns, and available Level 1 documentation, the system provides a triage recommendation with confidence score.'
    }
  ];

  const deploymentProcess = [
    {
      step: '01',
      title: 'Documentation Audit',
      description: 'We map your Hudu structure, Confluence spaces, and ticketing system with read-only access to understand information architecture.'
    },
    {
      step: '02',
      title: 'Workflow Configuration',
      description: 'Pre-built context workflows are customized for your organization\'s documentation patterns and operational structure.'
    },
    {
      step: '03',
      title: 'AI Model Configuration',
      description: 'Your chosen AI model (Ollama, Claude, GPT, or Gemini) is configured with operations-specific synthesis prompts and connected to your workflows.'
    },
    {
      step: '04',
      title: 'Team Onboarding',
      description: 'We train your Level 1 team on natural language queries and integrate fact sheet generation into your triage process.'
    }
  ];

  const impactMetrics = [
    {
      metric: '40%',
      label: 'Reduction in Level 1 ticket time',
      description: 'Tested in production operations environments'
    },
    {
      metric: '<30s',
      label: 'Average fact sheet generation',
      description: 'From query to comprehensive technical context'
    },
    {
      metric: '85%',
      label: 'Accuracy on known issues',
      description: 'When similar tickets exist in history'
    },
    {
      metric: '0',
      label: 'Client data leaks',
      description: 'Client-controlled AI with zero external transmission'
    }
  ];

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary-50 to-white pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold mb-8 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Home
            </button>

            <div className="text-center max-w-4xl mx-auto">
              <div className="bg-primary-100 w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-8">
                <Workflow className="h-12 w-12 text-primary-600" />
              </div>

              <div className="inline-block bg-primary-600 text-white text-sm font-bold px-4 py-2 rounded-full mb-6">
                READY TO DEPLOY
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Operational Triage & Intelligence
              </h1>

              <p className="text-2xl text-primary-600 font-medium mb-6">
                60-Second Technical Fact Sheets.
              </p>

              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                AI-powered triage and documentation synthesis for operations and support teams. Connect your knowledge base, ticketing
                systems, and live systems to generate instant technical context and best-effort triage—reducing Level 1
                noise by up to 40%.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={scrollToContact}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Request Implementation Audit
                </button>
              </div>

              <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Lock className="h-4 w-4" />
                <span>Read-only access · Your AI choice · Zero client data retention</span>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Operational Triage & Intelligence?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Stop wasting time on documentation and systems archaeology. Get instant technical context and solutions in front of your team, from the first second.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div
                    key={index}
                    className="bg-white p-8 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="bg-primary-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Impact Metrics */}
        <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Proven Results
              </h2>
              <p className="text-xl text-primary-100 max-w-3xl mx-auto">
                Real metrics from production operations deployments.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {impactMetrics.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl font-bold mb-2">
                    {item.metric}
                  </div>
                  <div className="text-xl font-semibold text-primary-100 mb-2">
                    {item.label}
                  </div>
                  <p className="text-primary-200 text-sm">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Real Questions. Instant Context.
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See how natural language queries eliminate documentation hunting.
              </p>
            </div>

            <div className="space-y-6">
              {useCases.map((useCase, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full flex-shrink-0 mt-1">
                      Q
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        {useCase.question}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        <span className="font-semibold text-primary-600">Answer: </span>
                        {useCase.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Demonstration Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                See It In Action
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Watch how your AI reads real business data before answering. No hallucinations.
                No guesses. Just facts from your systems, synthesized into answers you can trust.
              </p>
            </div>

            {/* Three-Step Visualization */}
            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              {/* Step 1: Question */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary-100 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-primary-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Step 1: Question
                  </span>
                </div>
                <div className="bg-primary-50 border-2 border-primary-200 p-6 rounded-2xl shadow-md min-h-[200px] flex items-center">
                  <p className="text-lg font-medium text-primary-900">
                    "Client VPN is down - what's the fix?"
                  </p>
                </div>
              </div>

              {/* Step 2: Context FactSheet */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary-100 rounded-lg">
                    <Terminal className="h-5 w-5 text-primary-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Step 2: Context Retrieval
                  </span>
                </div>
                <div className="bg-gray-900 border-2 border-gray-700 p-6 rounded-2xl shadow-xl min-h-[200px] overflow-auto">
                  <div className="font-mono text-sm space-y-1">
                    <div className="text-green-400 font-semibold">// Fetching Configuration Management Database</div>
                    <div className="text-blue-400">GET /api/v1/assets?client=Acme&type=firewall</div>
                    <div className="text-yellow-400">Response: &#123;</div>
                    <div className="text-gray-300">  asset: 'Primary Firewall',</div>
                    <div className="text-gray-300">  model: 'Fortinet FortiGate 60F',</div>
                    <div className="text-gray-300">  firmware: '7.2.4',</div>
                    <div className="text-gray-300">  ssl_cert_expires: '2024-01-15'</div>
                    <div className="text-gray-300">&#125;</div>
                    <div>&nbsp;</div>
                    <div className="text-green-400 font-semibold">// Querying Live System Status</div>
                    <div className="text-blue-400">GET https://firewall.acme.local/api/v2/monitor/vpn/ssl</div>
                    <div className="text-yellow-400">Response: &#123;</div>
                    <div className="text-gray-300">  status: 'down',</div>
                    <div className="text-gray-300">  error: 'SSL certificate expired',</div>
                    <div className="text-gray-300">  cert_valid_from: '2023-01-15',</div>
                    <div className="text-gray-300">  cert_valid_to: '2024-01-11',</div>
                    <div className="text-gray-300">  uptime_seconds: 0</div>
                    <div className="text-gray-300">&#125;</div>
                    <div>&nbsp;</div>
                    <div className="text-green-400 font-semibold">// Fetching Internal Documentation</div>
                    <div className="text-blue-400">GET /wiki/rest/api/content?title=VPN-Down</div>
                    <div className="text-yellow-400">Response: &#123;</div>
                    <div className="text-gray-300">  procedure: 'Check SSL cert expiry',</div>
                    <div className="text-gray-300">  path: 'Admin &gt; Certificate Management',</div>
                    <div className="text-gray-300">  common_cause: 'Expired certificates'</div>
                    <div className="text-gray-300">&#125;</div>
                  </div>
                </div>
              </div>

              {/* Step 3: Answer */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Step 3: Answer
                  </span>
                </div>
                <div className="bg-white border-2 border-primary-200 p-6 rounded-2xl shadow-lg min-h-[200px] flex items-center">
                  <p className="text-lg text-gray-800 leading-relaxed">
                    VPN is down due to expired SSL certificate (expired 4 days ago on Jan 11). Asset: Fortinet 60F. SOP: Navigate to Admin &gt; Certificate Management and renew the VPN certificate.
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy Pillars */}
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                Your Data Stays Yours
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
                      <Shield className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Read-Only API Access
                    </h4>
                    <p className="text-sm text-gray-600">
                      No data modified
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
                      <Database className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Zero Data Retention
                    </h4>
                    <p className="text-sm text-gray-600">
                      No data stored by GSCS
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
                      <Lock className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Private AI Model
                    </h4>
                    <p className="text-sm text-gray-600">
                      No data used for training
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Integrations Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What We Connect
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-3">
                Pre-engineered integrations for operations documentation systems.
              </p>
              <p className="text-sm text-gray-500">
                Don't see your software?{' '}
                <button
                  onClick={() => {
                    navigate('/bespoke-orchestration');
                    window.scrollTo(0, 0);
                  }}
                  className="text-primary-600 hover:text-primary-700 font-semibold underline"
                >
                  We have you covered
                </button>
                .
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {integrations.map((integration, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-2xl border border-primary-100"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {integration.name}
                  </h3>
                  <ul className="space-y-3">
                    {integration.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Deployment Process */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Deployment Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From audit to team onboarding in 2-3 weeks.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {deploymentProcess.map((process, index) => (
                <div key={index} className="text-center">
                  <div className="bg-primary-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {process.title}
                  </h3>
                  <p className="text-gray-600">
                    {process.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Privacy Guarantee */}
        <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Lock className="h-16 w-16 text-primary-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Your Clients' Data. Your AI. Your Control.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              We never store, export, or transmit client documentation. All integrations are read-only.
              Your chosen AI model (self-hosted or cloud-based under your API keys) processes queries under your control.
              Whether you utilize industry leaders like Gemini, ChatGPT, and Claude or private, local models for maximum security,
              our system remains the same: We build the pipes; you hold the keys.
              No data sent to us. No third-party access to your client information.
              You own the triage intelligence layer—we just build it.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
                <span>Read-Only APIs</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
                <span>Zero Data Retention</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
                <span>Client-Controlled AI</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary-600" />
                <span>Complete Ownership</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Deploy Operational Triage & Intelligence?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Let's audit your documentation stack and show you what 60-second fact sheets look like.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Request Implementation Audit
            </button>
          </div>
        </section>

        {/* Sticky CTA Button */}
        {showStickyButton && (
          <button
            onClick={scrollToContact}
            className="fixed bottom-8 right-8 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-full font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 animate-pulse z-50"
          >
            Get Started →
          </button>
        )}
      </div>
    </>
  );
};

export default OpsTriageIntelligence;
